import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { useDragLayer } from 'react-dnd';
import Modules from '~Components/Modules';
import { useStorage } from '~/Components/Storage';
import { useI18n } from '~Hooks';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { AdyenStoreTerminals } from './__queries__';
import Terminal, { TerminalDragItem } from './Terminal';
import AddNew from './AddNew';
import { Box } from '~Styles';
import AvailableTerminals from './AvailableTerminals';
import Heading from '~ComponentsImproved/Typography/Heading';
import { DragTypes } from '~ComponentsImproved/TSR/Types';

const Wrapper = styled.div`
  width: 100%;
  padding: 8px 32px;
`;

const InnerWrapper = styled.div`
  padding-bottom: 40px;
`;

const DragPreviewCard = styled.div`
  background-color: white;
  padding: 12px 16px;
  border-radius: 3px;
  border: 2px solid var(--colorBlueGray300);
  color: var(--colorBlueGray1200);
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.25);
  font-size: 14px;
  white-space: nowrap;
`;

// Floating drag preview that follows the cursor/finger
const TerminalDragLayer = () => {
  const { clientOffset, initialOffset, isDragging, item } = useDragLayer((monitor) => ({
    clientOffset: monitor.getClientOffset(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as TerminalDragItem | null,
  }));

  if (!isDragging || !item || item.type !== DragTypes.TERMINAL || !clientOffset || !initialOffset) {
    return null;
  }

  // Offset so the preview appears centered on the grab point
  const x = clientOffset.x - 40;
  const y = clientOffset.y - 20;

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100,
        left: 0,
        top: 0,
        transform: `translate3d(${x}px, ${y}px, 0)`,
      }}>
      <DragPreviewCard>
        <b>{item.displayName || item.id}</b>
      </DragPreviewCard>
    </div>
  );
};

const ADYEN_STORE_TERMINALS_QUERY = gql`
  query AdyenStoreTerminals {
    operativeViewer {
      id

      adyenStore {
        id
        terminals {
          id
        }
      }
    }
  }
`;

const Adyen = () => {
  const [, , venueId] = location.hash.split('/');
  const { i18n } = useI18n();
  const [storage, updateStorage] = useStorage();
  const adyenTerminals = storage.adyenTerminals;

  const adyenStoreTerminals = useQuery<AdyenStoreTerminals>(ADYEN_STORE_TERMINALS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      venueId,
    },
  });

  function removeAdyenTerminal(id: string) {
    updateStorage({
      adyenTerminals: adyenTerminals?.filter((terminal) => terminal.POIID !== id),
    });
  }

  const moveTerminal = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const updated = [...adyenTerminals];
      const [dragged] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, dragged);
      updateStorage({ adyenTerminals: updated });
    },
    [adyenTerminals, updateStorage],
  );

  const availableVenueTerminals =
    adyenStoreTerminals?.data?.operativeViewer?.adyenStore?.terminals || [];

  const allowAddManually = adyenStoreTerminals.data && availableVenueTerminals.length === 0;

  return (
    <Wrapper>
      <Modules />

      <InnerWrapper>
        {allowAddManually && (
          <Box marginBottom="xl">
            <Heading level={4} bottom={8} color="#fff">
              {i18n('Settings.AddNewTerminal')}
            </Heading>
            <AddNew />
          </Box>
        )}

        {adyenTerminals?.length > 0 && (
          <Box marginBottom="xl">
            <Heading level={4} bottom={8} color="#fff">
              {i18n('Settings.AddedTerminals')}
            </Heading>
            <TerminalDragLayer />
            {adyenTerminals.map((terminal, index) => (
              <Terminal
                key={terminal.POIID}
                id={terminal.POIID}
                index={index}
                isPrimary={index === 0 && adyenTerminals.length > 1}
                onRemove={() => removeAdyenTerminal(terminal.POIID)}
                onMoveTerminal={moveTerminal}
              />
            ))}
          </Box>
        )}

        <AvailableTerminals
          addedTerminals={adyenTerminals}
          availableVenueTerminals={availableVenueTerminals}
        />
      </InnerWrapper>
    </Wrapper>
  );
};

export default React.memo(Adyen);
